import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Globe } from "lucide-react";

/**
 * Small dropdown in the user panel that switches the UI language.
 * - Applies i18n change immediately (no reload).
 * - Persists the selection on the user profile via PUT /api/users/{id}.
 * - Currently exposes IT and EN (the fully-translated locales).
 */
const LANGS = [
  { code: "it", label: "Italiano" },
  { code: "en", label: "English" },
];

export function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const current = i18n.language?.slice(0, 2) || "it";

  if (!user?.id) return null;

  const change = async (code) => {
    if (code === current) return;
    i18n.changeLanguage(code);
    document.documentElement.lang = code;
    try {
      await api.put(`/users/${user.id}`, { language: code });
      await refreshUser?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="mt-1 flex items-center gap-1.5 px-2" data-testid="lang-switcher">
      <Globe className="h-3.5 w-3.5 text-zinc-400" strokeWidth={1.75} />
      <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">{t("common.language")}</span>
      {LANGS.map((l) => (
        <button
          key={l.code}
          onClick={() => change(l.code)}
          data-testid={`lang-${l.code}`}
          className={`text-[11px] px-1.5 py-0.5 rounded-sm border transition-colors ${
            current === l.code
              ? "bg-brand text-white border-brand"
              : "bg-white text-zinc-600 border-zinc-200 hover:border-brand hover:text-brand"
          }`}
          title={l.label}
        >
          {l.code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
